/* Types et constantes du CRM d'onboarding (table crm_contacts). */
import type { BillingChannel, ReimbursementPeriod } from '../../../supabase/functions/_shared/crmTodo';
export type { BillingChannel } from '../../../supabase/functions/_shared/crmTodo';
export type { ReimbursementPeriod } from '../../../supabase/functions/_shared/crmTodo';
export { PERIOD_LABELS } from '../../../supabase/functions/_shared/crmTodo';

export type Stage = 'lead' | 'contract_sent' | 'contract_signed' | 'sibelga' | 'active';
export type Role = 'consumer' | 'producer' | 'prosumer';
export type EntityType = 'physique' | 'morale';
export type Source = 'manuel' | 'formulaire';
export type VatRegime = 'assujetti' | 'non_assujetti' | 'franchise';
export type MeterType = 'intelligent' | 'mecanique';
export type WaitingOn = 'client' | 'sunisup';
/** Mandat de domiciliation SEPA : actif ou révoqué (vide : aucun, ou envoyé et en attente de signature). */
export type MandateStatus = 'active' | 'revoked';
/** Statut du dossier chez Sibelga (plusieurs choix possibles). */
export type SibelgaStatus = 'submitted' | 'meter_replacement' | 'contract_mismatch';

export const SIBELGA_STATUS_ORDER: SibelgaStatus[] = ['submitted', 'meter_replacement', 'contract_mismatch'];
export const SIBELGA_STATUS_LABELS: Record<SibelgaStatus, string> = {
  submitted: 'Soumis',
  meter_replacement: 'Compteur à remplacer',
  contract_mismatch: 'Contrat ne correspond pas',
};

export const WAITING_LABELS: Record<WaitingOn, string> = {
  client: 'En attente du client',
  sunisup: 'En attente de Sun Is Up',
};

/** Un point de comptage. Un membre signe un seul contrat pour tous ses EAN. */
export interface Meter {
  /** Nom du participant lié à cet EAN (saisi à la main, comme dans le formulaire participant de la page admin). */
  name: string | null;
  ean_code: string;
  meter_address: string | null;
  meter_type: MeterType | null;
  /** Fiche participants créée au passage en « Membre actif » (facturation). */
  participant_id: string | null;
}

export interface CrmContact {
  id: string;
  stage: Stage;
  source: Source;
  role: Role | null;
  entity_type: EntityType | null;

  first_name: string | null;
  last_name: string | null;
  birth_date: string | null;
  national_register: string | null;

  company_name: string | null;
  legal_form: string | null;
  company_number: string | null;
  representative: string | null;
  representative_title: string | null;

  email: string | null;
  phone: string | null;
  domicile_address: string | null;
  billing_email: string | null;
  billing_address: string | null;

  meters: Meter[];

  vat_regime: VatRegime | null;
  iban: string | null;
  account_holder: string | null;
  /** Producteur ou prosumer : puissance crête de l'installation (kWc) et droit sur l'installation (contrat). */
  installed_power_kwc: number | null;
  installation_right: InstallationRight | null;

  /* Domiciliation SEPA (particulier consommateur) : IBAN et titulaire = iban et account_holder. */
  bic: string | null;
  /** Accord du membre pour payer ses factures par domiciliation (formulaire ou fiche). */
  sepa_consent: boolean;
  /** Référence SIU-AAAA-NNNNN, attribuée par la base au premier envoi du mandat. */
  mandate_reference: string | null;
  mandate_sent_at: string | null;
  mandate_signed_at: string | null;
  mandate_status: MandateStatus | null;
  mandate_revoked_at: string | null;
  signed_mandate_path: string | null;

  /** Canal de facturation (vide : automatique) et copie e-mail au membre d'une facture Peppol. */
  billing_channel: BillingChannel | null;
  billing_copy_to_member: boolean;

  /* Estimation des économies demandée dans le formulaire (particulier consommateur ou prosumer). */
  estimate_requested: boolean;
  housing_type: HousingType | null;
  annual_consumption_kwh: number | null;
  /** Montant annuel payé pour l'électricité (€ TVAC), facultatif. */
  annual_cost_eur: number | null;

  price_cents_kwh: number | null;
  sell_price_cents_kwh: number | null;
  annual_fee: number | null;
  contract_sent_at: string | null;
  contract_signed_at: string | null;
  signed_contract_path: string | null;

  sibelga_encoded_at: string | null;
  /** Étape « Encodé Sibelga » : soumis, compteur à remplacer, contrat ne correspond pas. */
  sibelga_status: SibelgaStatus[];
  sharing_operation_id: string | null;

  sharing_start_date: string | null;
  sharing_price_cents_kwh: number | null;
  sharing_sell_price_cents_kwh: number | null;
  /** Taux de TVA écrit dans les fiches participants (fraction : 0, 0.06, 0.21). */
  sharing_vat_rate: number | null;
  /** Producteur ou prosumer : période de remboursement (par défaut trimestrielle). */
  reimbursement_period: ReimbursementPeriod | null;
  /** Pipeline : en attente du client ou à traiter par Sun Is Up (todolist). */
  waiting_on: WaitingOn | null;
  waiting_note: string | null;
  waiting_since: string | null;
  groupe: string | null;

  invitation_sent_at: string | null;
  onboarding_sent_at: string | null;

  questionnaire_submission_id: string | null;
  /** Date d'envoi du lien vers le formulaire d'adhésion. */
  questionnaire_sent_at: string | null;
  notes: string | null;
  archived_at: string | null;
  /** Société rattachée à la carte d'un groupe existant au passage en « Membre actif ». */
  merged_into_id: string | null;
  created_at: string;
  updated_at: string;
}

/** Champs modifiables depuis la fiche. */
export type CrmContactDraft = Partial<Omit<CrmContact, 'id' | 'created_at' | 'updated_at'>>;

export interface SharingOperation {
  id: string;
  name: string;
}

/** Compte plateforme du membre : approuvé, créé mais en attente de validation, ou aucun. */
export type AccountState = 'approved' | 'pending';

/** Données existantes servant aux suggestions (comme la fenêtre de création d'un participant). */
export interface Directory {
  /** Groupes existants (participants et CRM), avec leur nombre d'EAN dans les participants. */
  groups: { name: string; eanCount: number }[];
  companyNames: string[];
  /** N° BCE (format « BE 0123.456.789 ») → dénomination déjà utilisée. */
  companyNumberToName: Record<string, string>;
}

/** Fiche de la table participants (celle du formulaire participant de la page admin), colonnes utilisées par le CRM. */
export interface ParticipantRow {
  id: string;
  name: string | null;
  address: string | null;
  ean_code: string | null;
  type: Role | null;
  email: string | null;
  send_email: string | null;
  company_name: string | null;
  company_number: string | null;
  groupe: string | null;
  entry_date: string | null;
  sharing_operation_id: string | null;
  shared_energy_formula_type: 'fixed' | 'variable' | null;
  shared_energy_price: number | null;
  prosumer_buy_price: number | null;
  prosumer_sell_price: number | null;
  adhesion_fee: number | null;
  vat_rate: number | null;
  account_status: string | null;
  reimbursement_period: ReimbursementPeriod | null;
}

export const PARTICIPANT_COLUMNS =
  'id, name, address, ean_code, type, email, send_email, company_name, company_number, groupe, entry_date, sharing_operation_id, '
  + 'shared_energy_formula_type, shared_energy_price, prosumer_buy_price, prosumer_sell_price, adhesion_fee, vat_rate, account_status, '
  + 'reimbursement_period';

export type TemplateKey = 'contract' | 'invitation' | 'welcome' | 'questionnaire' | 'mandate';

export interface MessageTemplate {
  key: TemplateKey;
  label: string;
  subject: string;
  body: string;
  updated_at: string;
}

export const STAGES: { key: Stage; label: string; accent: string; dot: string }[] = [
  { key: 'lead', label: 'Leads', accent: 'border-neutral-300', dot: 'bg-neutral-400' },
  { key: 'contract_sent', label: 'Contrat envoyé', accent: 'border-blue-300', dot: 'bg-blue-500' },
  { key: 'contract_signed', label: 'Contrat signé', accent: 'border-violet-300', dot: 'bg-violet-500' },
  { key: 'sibelga', label: 'Encodé Sibelga', accent: 'border-amber-300', dot: 'bg-amber-500' },
  { key: 'active', label: 'Membre actif', accent: 'border-emerald-300', dot: 'bg-emerald-500' },
];

export const STAGE_ORDER: Stage[] = STAGES.map(s => s.key);

export const STAGE_INDEX: Record<Stage, number> = {
  lead: 0,
  contract_sent: 1,
  contract_signed: 2,
  sibelga: 3,
  active: 4,
};

export const ROLE_LABELS: Record<Role, string> = {
  consumer: 'Consommateur',
  producer: 'Producteur',
  prosumer: 'Prosumer',
};

export const ROLE_BADGES: Record<Role, string> = {
  consumer: 'bg-blue-100 text-blue-700',
  producer: 'bg-amber-100 text-amber-700',
  prosumer: 'bg-emerald-100 text-emerald-700',
};

export const ENTITY_LABELS: Record<EntityType, string> = {
  physique: 'Personne physique',
  morale: 'Personne morale',
};

export const VAT_LABELS: Record<VatRegime, string> = {
  assujetti: 'Assujetti (21 %)',
  non_assujetti: 'Non assujetti (0 %)',
  franchise: 'Franchise',
};

/** Taux de TVA écrit dans participants.vat_rate (fraction). */
export const VAT_RATES: Record<VatRegime, number> = {
  assujetti: 0.21,
  non_assujetti: 0,
  franchise: 0,
};

/** Modèle de convention : un par type de membre et par type de personne (bucket contract-templates). */
export interface ContractTemplateSlot {
  role: Role;
  entity: EntityType;
  file: string;
}

const ROLE_FILE_PREFIX: Record<Role, string> = { consumer: 'consommateur', producer: 'producteur', prosumer: 'prosumer' };

export const CONTRACT_TEMPLATES: ContractTemplateSlot[] = (['consumer', 'producer', 'prosumer'] as Role[]).flatMap(role =>
  (['physique', 'morale'] as EntityType[]).map(entity => ({
    role, entity, file: `${ROLE_FILE_PREFIX[role]}_${entity === 'morale' ? 'pm' : 'pp'}.pdf`,
  })));

/** Modèles du mandat SEPA (particulier consommateur). */
export const SEPA_CONTRACT_TEMPLATE = 'consommateur_pp_sepa.pdf';
export const MANDATE_TEMPLATE = 'mandat_sepa.pdf';
export const EXTRA_CONTRACT_TEMPLATES: { file: string; label: string; help: string }[] = [
  { file: SEPA_CONTRACT_TEMPLATE, label: 'Consommateur · personne physique · avec mandat SEPA', help: 'Contrat envoyé avec l’interrupteur « Inclure le mandat SEPA »' },
  { file: MANDATE_TEMPLATE, label: 'Mandat SEPA seul', help: 'Action « Envoyer le mandat SEPA » d’un membre déjà actif' },
];

/** Ancien modèle unique par type de membre, utilisé tant que le modèle PP / PM n'est pas déposé. */
export const LEGACY_CONTRACT_TEMPLATE_FILES: Record<Role, string> = {
  consumer: 'consommateur.pdf',
  producer: 'producteur.pdf',
  prosumer: 'prosumer.pdf',
};

export type InstallationRight = 'owner' | 'usage';
export const INSTALLATION_RIGHT_LABELS: Record<InstallationRight, string> = {
  owner: 'Propriétaire',
  usage: 'Titulaire d’un droit d’usage',
};

export type HousingType = 'maison' | 'appartement' | 'autre';
export const HOUSING_LABELS: Record<HousingType, string> = { maison: 'Maison', appartement: 'Appartement', autre: 'Autre' };

export const TEMPLATE_VARIABLES: { key: string; label: string }[] = [
  { key: '{{nom}}', label: 'Nom affiché : dénomination (société) ou prénom et nom (particulier)' },
  { key: '{{prenom}}', label: 'Prénom (particulier, ou représentant(e) de la société)' },
  { key: '{{nom_famille}}', label: 'Nom (particulier, ou représentant(e) de la société)' },
  { key: '{{societe}}', label: 'Dénomination de la société' },
  { key: '{{forme_juridique}}', label: 'Forme juridique (société)' },
  { key: '{{n_entreprise}}', label: 'N° BCE (société)' },
  { key: '{{qualite}}', label: 'Qualité du représentant(e) (société)' },
  { key: '{{email}}', label: 'E-mail' },
  { key: '{{telephone}}', label: 'Téléphone' },
  { key: '{{adresse}}', label: 'Domicile (particulier) ou siège social (société)' },
  { key: '{{email_facturation}}', label: 'E-mail de facturation' },
  { key: '{{adresse_facturation}}', label: 'Adresse de facturation' },
  { key: '{{ean}}', label: 'Premier code EAN' },
  { key: '{{liste_ean}}', label: 'Tous les codes EAN' },
  { key: '{{date_debut}}', label: 'Début du partage' },
  { key: '{{prix}}', label: 'Prix (c€/kWh)' },
  { key: '{{prix_vente}}', label: 'Prix de vente prosumer' },
  { key: '{{cotisation}}', label: 'Cotisation (€/an)' },
  { key: '{{operation}}', label: 'Opération de partage' },
  { key: '{{reference_mandat}}', label: 'Référence du mandat SEPA' },
  { key: '{{economie_estimee}}', label: 'Économie estimée (€/an, envoi du contrat)' },
  { key: '{{consommation_annuelle}}', label: 'Consommation annuelle (kWh)' },
];

/**
 * Variables des modèles d'e-mail alimentées par un champ de la fiche (affichées à côté du champ).
 * {{prix}} et {{prix_vente}} : prix appliqué au partage, sinon prix proposé.
 */
export function templateVarsFor(key: string, entity: EntityType | null | undefined): string[] {
  const pm = entity === 'morale';
  if (key === 'meters.0.ean_code') return ['{{ean}}', '{{liste_ean}}'];
  if (/^meters\.\d+\.ean_code$/.test(key)) return ['{{liste_ean}}'];
  switch (key) {
    case 'first_name': return ['{{prenom}}'];
    case 'last_name': return pm ? ['{{nom_famille}}'] : ['{{nom_famille}}', '{{nom}}'];
    case 'company_name': return pm ? ['{{societe}}', '{{nom}}'] : ['{{societe}}'];
    case 'email': return ['{{email}}'];
    case 'phone': return ['{{telephone}}'];
    case 'domicile_address': return ['{{adresse}}'];
    case 'billing_email': return ['{{email_facturation}}'];
    case 'billing_address': return ['{{adresse_facturation}}'];
    case 'legal_form': return ['{{forme_juridique}}'];
    case 'company_number': return ['{{n_entreprise}}'];
    case 'representative_title': return ['{{qualite}}'];
    case 'mandate_reference': return ['{{reference_mandat}}'];
    case 'price_cents_kwh': case 'sharing_price_cents_kwh': return ['{{prix}}'];
    case 'sell_price_cents_kwh': case 'sharing_sell_price_cents_kwh': return ['{{prix_vente}}'];
    case 'annual_fee': return ['{{cotisation}}'];
    case 'sharing_start_date': return ['{{date_debut}}'];
    case 'sharing_operation_id': return ['{{operation}}'];
    case 'annual_consumption_kwh': return ['{{consommation_annuelle}}'];
    default: return [];
  }
}

/** Domiciliation SEPA : réservée aux particuliers consommateurs. */
export const isSepaEligible = (c: Pick<CrmContactDraft, 'role' | 'entity_type'>) => c.role === 'consumer' && c.entity_type === 'physique';

export type MandateState = 'none' | 'pending' | 'active' | 'revoked';
export const MANDATE_STATE_LABELS: Record<MandateState, string> = {
  none: 'Aucun mandat',
  pending: 'En attente de signature',
  active: 'Actif',
  revoked: 'Révoqué',
};

export function mandateState(c: Pick<CrmContactDraft, 'mandate_reference' | 'mandate_status'>): MandateState {
  if (c.mandate_status === 'active') return 'active';
  if (c.mandate_status === 'revoked') return 'revoked';
  return c.mandate_reference ? 'pending' : 'none';
}

type NameFields = Pick<CrmContact, 'entity_type' | 'company_name' | 'first_name' | 'last_name' | 'email'> &
  Partial<Pick<CrmContact, 'groupe' | 'stage' | 'merged_into_id'>>;

/** Carte d'un groupe : membre actif avec un nom de groupe (hors fiche d'une société rattachée à ce groupe). */
export function isGroupCard(c: Partial<Pick<CrmContact, 'groupe' | 'stage' | 'merged_into_id'>> | null | undefined): boolean {
  return !!c && c.stage === 'active' && !!c.groupe?.trim() && !c.merged_into_id;
}

/** Nom affiché : la carte d'un groupe s'affiche sous le nom du groupe. */
export function displayName(c: NameFields): string {
  if (isGroupCard(c)) return c.groupe!.trim();
  return signatoryName(c);
}

/** Nom de la société ou de la personne (celle qui signe le contrat). */
export function signatoryName(c: NameFields): string {
  if (c.entity_type === 'morale' && c.company_name) return c.company_name;
  const person = [c.first_name, c.last_name].filter(Boolean).join(' ').trim();
  return person || c.company_name || c.email || 'Sans nom';
}

/** N° BCE au format des fiches participants : « BE 0123.456.789 ». */
export function formatBce(value: string | null | undefined): string | null {
  const d = (value ?? '').replace(/\D/g, '');
  if (!d) return null;
  return d.length === 10 ? `BE ${d.slice(0, 4)}.${d.slice(4, 7)}.${d.slice(7)}` : (value ?? '').trim();
}

/** Cotisation annuelle par défaut (€/an HTVA) : 5 € pour un particulier, 50 € pour une entreprise. */
export const DEFAULT_ANNUAL_FEE = { physique: 5, morale: 50 } as const;

export function defaultAnnualFee(entity: EntityType | null | undefined): number {
  return entity === 'morale' ? DEFAULT_ANNUAL_FEE.morale : DEFAULT_ANNUAL_FEE.physique;
}

/** Changement personne physique / morale : la cotisation suit si elle était vide ou à la valeur par défaut. */
export function feeAfterEntityChange(fee: number | null | undefined, from: EntityType | null | undefined, to: EntityType): number | null {
  if (fee === null || fee === undefined || Number(fee) === defaultAnnualFee(from)) return defaultAnnualFee(to);
  return Number(fee);
}

export function emptyMeter(): Meter {
  return { name: null, ean_code: '', meter_address: null, meter_type: null, participant_id: null };
}

/** Normalise la colonne meters lue en base (jsonb). */
export function normalizeMeters(value: unknown): Meter[] {
  if (!Array.isArray(value)) return [];
  return value.map(v => {
    const m = (v ?? {}) as Partial<Meter>;
    return {
      name: typeof m.name === 'string' && m.name.trim() !== '' ? m.name : null,
      ean_code: String(m.ean_code ?? ''),
      meter_address: m.meter_address ?? null,
      meter_type: m.meter_type === 'intelligent' || m.meter_type === 'mecanique' ? m.meter_type : null,
      participant_id: m.participant_id ?? null,
    };
  });
}
